import { differenceInCalendarDays, format, isTomorrow, isYesterday, parseISO, isToday, isValid } from 'date-fns';
import { getDays, localeMap } from '../constants/Constants';
import i18n from '../i18n';

const date_format = 'dd MMM yyyy';
const long_date_format = 'd. MMMM yyyy';
const group_date_format = 'EEEE, d MMM yyyy';

/**
 * Absolute date, e.g. "17. June 2026" (localised: "17. Juni 2026").
 *
 * Unlike format_day this never goes relative ("yesterday", "Monday"): the
 * inactivity and teacher lists are scanned as columns of dates, where a mix of
 * relative and absolute wording is not comparable at a glance.
 *
 * Accepts an ISO string, a Date, or an epoch number. Returns '' for anything
 * unparseable so callers can fall back to their own placeholder.
 */
export const format_date_long = (date, locale = i18n.language) => {
	if (date === null || date === undefined || date === '') return '';
	const parsed = typeof date === 'string' ? parseISO(date) : new Date(date);
	if (!isValid(parsed)) return '';
	try {
		return format(parsed, long_date_format, { locale: localeMap[locale] });
	} catch {
		return '';
	}
};

/**
 * Header for a list grouped by day, e.g. "Today" or "Thursday, 24 Sep 2026".
 *
 * Only the three words that need no date to be understood stay relative; every
 * other day carries its full date. format_day would render "Thursday" directly
 * above "22 Sep 2026", which reads like two different kinds of grouping.
 */
export const format_group_day = (date, locale = i18n.language) => {
	if (date === null || date === undefined || date === '') return '';
	const parsed = typeof date === 'string' ? parseISO(date) : new Date(date);
	if (!isValid(parsed)) return '';
	if (isToday(parsed)) return i18n.t('today');
	if (isYesterday(parsed)) return i18n.t('yesterday');
	if (isTomorrow(parsed)) return i18n.t('tomorrow');
	try {
		return format(parsed, group_date_format, { locale: localeMap[locale] });
	} catch {
		return '';
	}
};

export const format_day = (date, hours = true, locale = i18n.language) => {
	if (typeof date == 'string') date = parseISO(date);
	try {
		if (isToday(date)) {
			return ' ' + i18n.t('today') + ` ${hours ? format(date, ' HH:mm') : ''}`;
		} else if (isTomorrow(date)) {
			return ' ' + i18n.t('tomorrow') + ` ${hours ? format(date, ' HH:mm') : ''}`;
		} else if (isYesterday(date)) {
			return ' ' + i18n.t('yesterday') + ` ${hours ? format(date, ' HH:mm') : ''}`;
		} else if (Math.abs(differenceInCalendarDays(date, new Date())) > 6) {
			// Absolute in both directions: a weekday name is only unambiguous within the surrounding week.
			return format(date, date_format + (hours ? ' HH:mm' : ''), { locale: localeMap[locale] });
		} else {
			const day = new Date(date).getDay();
			return `${getDays()[day]}${hours ? format(date, ' HH:mm') : ''}`;
		}
	} catch {
		return '';
	}
};
